import classNames from 'classnames'
import { Node as PmNode } from 'prosemirror-model'
import { EditorState, Plugin } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { renderToStaticMarkup, renderToString } from 'react-dom/server'

import { util } from '@showrunner/codex'
import { schema } from '@showrunner/prose-schemas'
import { schemas, ZInfer } from '@showrunner/scrapi'

import { actSlugTrackerPlugin } from '#choo-app/lib/editor/plugins/act-slug-tracker'
import { blockInfoFactory } from '#choo-app/lib/editor/plugins/block-info'
import { characterContdsPlugin } from '#choo-app/lib/editor/plugins/character'
import { configDataPlugin } from '#choo-app/lib/editor/plugins/configData'
import type { PageBreakScriptInfo } from '#choo-app/lib/editor/plugins/inlinePageBreaks'
import {
  getOmittableBlockIdsState,
  omittableBlockIdsPlugin,
} from '#choo-app/lib/editor/plugins/omittable-block-ids'
import { createPaginationPlugin } from '#choo-app/lib/editor/plugins/pagination'
import { materializePrintPages } from '#choo-app/lib/editor/plugins/printing/materializeProseSplits'
import { inlineChangesPrintPlugin } from '#choo-app/lib/editor/plugins/revision-asterisks'
import {
  getLoadedRevisionsState,
  highestRevisionForRange,
  revisionsPluginForPrinting,
  RevisionsState,
  revisionsStateFromDocument,
  RevisionTracker,
} from '#choo-app/lib/editor/plugins/revisions'
import { deleteTitlePage } from '#choo-app/lib/editor/plugins/titlePageView/commands'
import { TwoColumnScript } from '#components/TwoColumnScript'
import { buildFormatStyleString } from '#layouts/Script/buildFormatStyles'
import {
  DEFAULT_SCENE_CONTD_FOOTER_PREFIX,
  DEFAULT_SCENE_CONTD_HEADER_PREFIX,
  formatSeconds,
  getPrintDate,
  getPrintTimestamp,
} from '#util'
import type { Comparison } from '#util/diffing/NodeDiff'
import {
  appendFontFallback,
  BlockFormats,
  FontCode,
  FontCodeMap,
} from '#util/formats'
import { getPageLayout, PageLayout } from '#util/PageLayout'
import * as cssStrings from '#util/printing/cssStrings'
import {
  createProsemirrorDoc,
  removeIgnorableBlocks,
} from '#util/prosemirrorHelpers'
import { RundownRowData, ScriptJson } from '#util/ScriptoApiClient/types'
import {
  PageLayout as PageDimensions,
  PageMarginSizes,
  ScriptPrintPreferences,
} from '#util/zodSchemas/printPreferences'

import { buildHtmlForPrince } from './buildHtmlForPrince'
import { buildPageCssVars } from './buildPageCssVars'
import * as constants from './constants'
import { ScriptHeaderAndFooter } from './HeaderAndFooter'
import { IPrincePrintStrategy } from './types'

// In printing, we extend the script format to include whether character
// continueds should follow page boundaries. Unclear yet whether this should be
// a new script format property or piggyback on top of the existing character
// continueds, so let's fudge it with an extension to the type used in printing
export interface ScriptFormatConfiguration extends ZInfer<
  typeof schemas.scriptFormats.ScriptFormatDefinition
> {
  dialogueMoresAndContinueds?: boolean
}

// when we print a whole script, we create a snapshot
// and pull the doc and formatDefinition from the immutable artifact
// when we print a section, we pass the same props directly
type ScriptPrintStrategyParams = {
  doc: ScriptJson
  formatDefinition: ScriptFormatConfiguration
  prefs: ScriptPrintPreferences
  timestamp: string
  date: string
  title: string
  readRate?: number
  duration?: string
  rundownRowData?: RundownRowData
}

type OneColumnPrintStrategyParams = ScriptPrintStrategyParams & {
  revisionTracker?: RevisionTracker
  revisionShowPageColors?: boolean
}

type AsteriskPrintStrategyParams = {
  comparison: Comparison
  useRightSideMarks?: boolean
  highlightAdditions?: boolean
  showDeletions?: boolean
} & OneColumnPrintStrategyParams

const generateOneColumnStyles = ({
  monochrome,
  blockFormats,
  pageLayout,
  fontCode = 'courier-prime',
  sceneContdFooterPrefix,
  sceneContdHeaderPrefix,
}: {
  monochrome: boolean
  blockFormats: BlockFormats
  pageLayout: PageLayout
  fontCode: FontCode | undefined
  sceneContdFooterPrefix: string
  sceneContdHeaderPrefix: string
}) => `
${buildFormatStyleString({ blockFormats, monochrome, pageLayout })}
${cssStrings.editorCSS}
${cssStrings.oneColumnCSS}
:root {
  --script-font-family: ${appendFontFallback(FontCodeMap[fontCode])};
  --scene-contd-footer-prefix: '${sceneContdFooterPrefix}';
  --scene-contd-header-prefix: '${sceneContdHeaderPrefix}';
}
`

const generateTwoColumnWithTitlePagesStyles = ({
  monochrome,
  blockFormats,
  pageLayout,
  fontCode = 'courier-prime',
}: {
  monochrome: boolean
  blockFormats: BlockFormats
  pageLayout: PageLayout
  fontCode: FontCode | undefined
}) => `
${buildFormatStyleString({ blockFormats, monochrome, pageLayout })}
${cssStrings.editorCSS}
${cssStrings.twoColumnCSS}
:root {
  --script-font-family: ${appendFontFallback(FontCodeMap[fontCode])};
}`

const isScreenplay = (doc: PmNode | ScriptJson): boolean =>
  doc.attrs?.docType === 'screenplay'

// Route Prince margin boxes to per-page running elements materialized in the
// structural print document.
const structuralHeaderFooterOverrideCSS = `
  @page {
    @top {
      content: element(printPageHeaderMargin);
      vertical-align: bottom;
    }
    @bottom {
      content: element(printPageFooterMargin);
      vertical-align: top;
    }
  }
`

// This abstract class is the common base for printing 1 or 2 column scripts and should contain
// any state or logic that is common across both
abstract class BaseScriptPrintStrategy implements IPrincePrintStrategy {
  protected prefs: ScriptPrintPreferences
  protected title: string
  protected timestamp: string
  protected date: string
  protected duration: string | undefined
  protected rundownRowData: RundownRowData | undefined
  protected doc: PmNode
  protected readonly scriptFormat: ScriptFormatConfiguration
  protected pageLayout: PageLayout
  protected readonly revisionsState: RevisionsState | undefined

  constructor({
    doc,
    formatDefinition,
    prefs,
    title,
    timestamp,
    date,
    duration,
    rundownRowData,
  }: ScriptPrintStrategyParams) {
    this.doc = createProsemirrorDoc(doc)
    this.scriptFormat = formatDefinition
    this.prefs = prefs
    this.timestamp = timestamp
    this.date = date
    this.title = title
    this.duration = duration
    this.rundownRowData = rundownRowData
    this.pageLayout = getPageLayout(formatDefinition.pageConfig)
    const parsedRevisions = revisionsStateFromDocument(this.doc)
    this.revisionsState =
      parsedRevisions.tag === 'Valid'
        ? parsedRevisions.revisionsState
        : undefined
  }

  // The One and Two-column strategies are responsible for implementing these
  // measurements for the margins
  abstract getMargins(): PageMarginSizes
  // any styles specific to the print format
  abstract generateFormatSpecificStyles(): string
  // the HTML that goes into the body
  abstract generateBody(): string

  generateHeadElements(): string {
    return constants.FONTS
  }

  protected getActiveRevisionLabel(): string | undefined {
    if (!this.revisionsState?.enabled) return undefined
    return this.revisionsState.revisions.active.label
  }

  protected getPaginationScriptInfo(): PageBreakScriptInfo {
    return {
      name: this.title,
      scriptType: isScreenplay(this.doc) ? 'screenplay' : 'variety',
      scriptPrintPrefs: this.prefs,
    }
  }

  // Subclasses that materialize headers/footers as structural per-page
  // elements (see structuralHeaderFooterOverrideCSS) handle first-page
  // suppression themselves and should opt out of this CSS-based approach.
  protected usesStructuralHeaderFooter(): boolean {
    return false
  }

  // this combines styles common to all script print html with
  // the format-specific ones provided by the subclass
  generateStyles() {
    return [
      this.usesStructuralHeaderFooter() || this.prefs.headers.showOnFirstPage
        ? ''
        : constants.HIDE_HEADERS_ON_FIRST_PAGE,
      this.usesStructuralHeaderFooter() || this.prefs.footers.showOnFirstPage
        ? ''
        : constants.HIDE_FOOTERS_ON_FIRST_PAGE,
      buildPageCssVars({
        layout: this.generatePageLayout(),
        title: this.title,
        timestamp: this.timestamp,
        date: this.date,
        monochrome: this.prefs.monochrome,
        duration: this.duration,
        activeRevision: this.getActiveRevisionLabel(),
        rundownRowData: this.rundownRowData,
      }),
      cssStrings.headerFooterCSS,
      this.generateFormatSpecificStyles(),
      cssStrings.titlePageCSS,
    ].join('\n')
  }

  generatePageLayout(): PageDimensions {
    const { pageHeightPx, pageWidthPx } = this.pageLayout
    const orientation = pageHeightPx > pageWidthPx ? 'portrait' : 'landscape'

    return {
      size: { height: `${pageHeightPx}px`, width: `${pageWidthPx}px` },
      margins: this.getMargins(),
      orientation,
    }
  }

  generateHeaderAndFooter() {
    return renderToString(<ScriptHeaderAndFooter prefs={this.prefs} />)
  }
}

class OneColumnPrintStrategy extends BaseScriptPrintStrategy {
  protected revisionTracker?: RevisionTracker
  protected revisionShowPageColors: boolean

  constructor(params: OneColumnPrintStrategyParams) {
    super(params)
    this.revisionTracker = params.revisionTracker
    this.revisionShowPageColors = params.revisionShowPageColors ?? false
  }

  getMargins(): PageMarginSizes {
    const { pageMarginsPx } = this.pageLayout
    return {
      left: `${pageMarginsPx.left}px`,
      right: `${pageMarginsPx.right}px`,
      top: `${pageMarginsPx.top}px`,
      bottom: `${pageMarginsPx.bottom}px`,
    }
  }

  generateFormatSpecificStyles() {
    return [
      generateOneColumnStyles({
        monochrome: this.prefs.monochrome,
        blockFormats: this.scriptFormat.blocks,
        fontCode: this.scriptFormat.fontCode,
        pageLayout: this.pageLayout,
        // TODO: we can easily make these configurable if someone ever asks
        // for it (ie: https://github.com/showrunner/scrapi/pull/115)
        sceneContdFooterPrefix: DEFAULT_SCENE_CONTD_FOOTER_PREFIX,
        sceneContdHeaderPrefix: DEFAULT_SCENE_CONTD_HEADER_PREFIX,
      }),
      this.usesStructuralHeaderFooter()
        ? structuralHeaderFooterOverrideCSS
        : '',
    ].join('\n')
  }

  protected usesStructuralHeaderFooter(): boolean {
    return this.scriptFormat.paginationType === 'inline'
  }

  // The structural per-page decorations (pageElements.ts) only render the
  // top/bottom slot rows, not the top-right-corner region — render it here,
  // still, alongside them.
  generateHeaderAndFooter() {
    return this.usesStructuralHeaderFooter()
      ? renderToString(<ScriptHeaderAndFooter prefs={this.prefs} onlyCorner />)
      : super.generateHeaderAndFooter()
  }

  protected getAdditionalPlugins(): Plugin[] {
    return []
  }

  generateBody() {
    const view = this.createView(this.buildPlugins())
    this.applyDocMutations(view)
    this.applyPostPaginationDecorations(view)
    return this.serialize(view)
  }

  private buildPlugins(): Plugin[] {
    const script = { scriptFormat: { definition: this.scriptFormat } }
    const plugins: Plugin[] = [
      configDataPlugin({ script }),
      omittableBlockIdsPlugin(),
      blockInfoFactory(),
      actSlugTrackerPlugin(),
    ]

    if (this.scriptFormat.moresContds?.enableCharContds) {
      plugins.push(
        characterContdsPlugin({
          script: { scriptFormat: { definition: this.scriptFormat } },
        }),
      )
    }

    if (this.scriptFormat.paginationType === 'inline') {
      plugins.push(
        createPaginationPlugin(this.getPaginationScriptInfo(), undefined, true),
      )
    }

    if (this.revisionTracker) {
      if (!this.revisionsState) {
        throw new Error('Print plugin requires valid revisions data')
      }
      plugins.push(
        revisionsPluginForPrinting({
          tracker: this.revisionTracker,
          showPageColors: this.revisionShowPageColors,
        }),
      )
    }

    plugins.push(...this.getAdditionalPlugins())

    return plugins
  }

  private createView(plugins: Plugin[]): EditorView {
    return new EditorView(null, {
      state: EditorState.create({ doc: this.doc, plugins, schema }),
      editable: () => false,
    })
  }

  private applyDocMutations(view: EditorView): void {
    if (!this.prefs.includeTitlePages) {
      deleteTitlePage(view.state, view.dispatch)
    }

    const blockIdsToOmit = getOmittableBlockIdsState(view.state)

    if (blockIdsToOmit.length > 0) {
      const tr = removeIgnorableBlocks(view.state, blockIdsToOmit)
      view.dispatch(tr)
    }
  }

  // Must run after all doc-mutating steps so pagination is final.
  private applyPostPaginationDecorations(view: EditorView): void {
    if (this.scriptFormat.paginationType === 'inline') {
      const showPageColors =
        getLoadedRevisionsState(view.state)?.showPageColors ?? false
      const decorations = materializePrintPages(view, {
        getCollatedRevision: (startPos, endPos) =>
          highestRevisionForRange(view.state, startPos, endPos)?.label,
        getPageColor: showPageColors
          ? (startPos, endPos) =>
              highestRevisionForRange(view.state, startPos, endPos)?.pageColor
          : undefined,
        headerFooter: {
          headers: this.prefs.headers,
          footers: this.prefs.footers,
          title: this.title,
          timestamp: this.timestamp,
          date: this.date,
          duration: this.duration,
          activeRevisionLabel: this.getActiveRevisionLabel(),
        },
      })
      view.setProps({ decorations: () => decorations })
    }
  }

  private serialize(view: EditorView): string {
    const payload = `
      <div contenteditable="false" translate="no" class="${this.getClasses()}">
        ${new XMLSerializer().serializeToString(view.dom)}
      </div>
    `

    view.destroy() // garbage collection

    return payload
  }

  private getClasses() {
    const { paginationType } = this.scriptFormat

    return classNames('ProseMirror', 'is-static', {
      [`is-${paginationType}`]: paginationType !== 'none',
      [`is-${this.doc.attrs.docType}`]: this.doc.attrs.docType,
    })
  }
}

class TwoColumnPrintStrategy extends BaseScriptPrintStrategy {
  constructor(params: ScriptPrintStrategyParams) {
    super(params)
  }

  getMargins = () => constants.TWO_COLUMN_MARGIN_SIZE

  generateFormatSpecificStyles() {
    if (this.prefs.includeTitlePages) {
      return generateTwoColumnWithTitlePagesStyles({
        monochrome: this.prefs.monochrome,
        blockFormats: this.scriptFormat.blocks,
        fontCode: this.scriptFormat.fontCode,
        pageLayout: this.pageLayout,
      })
    }
    return cssStrings.twoColumnCSS
  }

  generateBody() {
    const script = { scriptFormat: { definition: this.scriptFormat } }
    const plugins: Plugin[] = [configDataPlugin({ script })]

    if (this.scriptFormat.paginationType === 'inline') {
      plugins.push(createPaginationPlugin(undefined, undefined, true))
    }

    const view = new EditorView(null, {
      state: EditorState.create({ doc: this.doc, plugins, schema }),
      editable: () => false,
      nodeViews: {
        page: (node) => {
          const pageDoc = schema.nodes.doc.create(this.doc.attrs, [node])
          const scriptJson = pageDoc.toJSON() as ScriptJson

          const template = document.createElement('template')
          // eslint-disable-next-line no-unsanitized/property -- renderToStaticMarkup escapes all content
          template.innerHTML = renderToStaticMarkup(
            <TwoColumnScript
              prefs={this.prefs}
              scriptJson={scriptJson}
              blockFormats={this.scriptFormat.blocks}
            />,
          )

          const dom = template.content.firstChild as HTMLElement

          return { dom }
        },
      },
    })

    if (!this.prefs.includeTitlePages) {
      deleteTitlePage(view.state, view.dispatch)
    }

    const html = new XMLSerializer().serializeToString(view.dom)
    view.destroy()

    return html
  }
}

class OneColumnAsteriskPrintStrategy extends OneColumnPrintStrategy {
  private comparison: Comparison
  private useRightSideMarks: boolean
  private highlightAdditions: boolean
  private showDeletions: boolean

  constructor(params: AsteriskPrintStrategyParams) {
    super(params)
    this.comparison = params.comparison
    this.useRightSideMarks = params.useRightSideMarks ?? false
    this.highlightAdditions = params.highlightAdditions ?? true
    this.showDeletions = params.showDeletions ?? false
  }

  protected getAdditionalPlugins(): Plugin[] {
    return [
      inlineChangesPrintPlugin({
        comparison: this.comparison,
        useRightSideMarks: this.useRightSideMarks,
        highlightAdditions: this.highlightAdditions,
        showDeletions: this.showDeletions,
      }),
    ]
  }
}

// this is what is passed in to the helper functions
type PrintScriptParams = {
  doc: ScriptJson
  formatDefinition: ScriptFormatConfiguration
  prefs: ScriptPrintPreferences
  title: string
  readRate?: number
  rundownRowData?: RundownRowData
  revisionTracker?: RevisionTracker
  revisionShowPageColors?: boolean
}

// we can use the PrintScriptParams to generate duration & timestamp
const enrichWithComputedValues = <T extends PrintScriptParams>(
  params: T,
): T & { timestamp: string; date: string; duration?: string } => {
  const { doc, readRate } = params
  const timestamp = getPrintTimestamp()
  const date = getPrintDate()
  const duration =
    !isScreenplay(doc) && readRate
      ? formatSeconds(
          new util.ScriptBreakdown(createProsemirrorDoc(doc), readRate).timing
            .totalSeconds,
        )
      : undefined
  return {
    ...params,
    timestamp,
    date,
    duration,
  }
}

// build printable html for a script, either one or two column depending on the prefs
export const buildPrintableScriptHtml = (params: PrintScriptParams): string => {
  const { doc, prefs } = params
  const twoColumn = prefs.columns && !isScreenplay(doc)
  const strategyParams = enrichWithComputedValues(params)
  const strategy = twoColumn
    ? new TwoColumnPrintStrategy(strategyParams)
    : new OneColumnPrintStrategy(strategyParams)

  return buildHtmlForPrince(strategy)
}

interface BuildPrintableAsteriskHtmlParams extends PrintScriptParams {
  comparison: Comparison
  useRightSideMarks?: boolean
  highlightAdditions?: boolean
  showDeletions?: boolean
}

// build printable html for a script with asterisk revisions(change tracking) applied
// we couldn't use the same strategy as buildPrintableScriptHtml because we need to apply the inlineChangesPrintPlugin
export const buildPrintableAsteriskHtml = (
  params: BuildPrintableAsteriskHtmlParams,
): string => {
  const strategy = new OneColumnAsteriskPrintStrategy(
    enrichWithComputedValues(params),
  )

  return buildHtmlForPrince(strategy)
}
